<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import { maskSlug } from "@/common/utils/mask";

import type { SlugType, SlugPayloadType } from "@/common/types/slug";

import { baseInput, baseSelect } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "update:isMain", "salve:data"]);

const props = defineProps<{
  data?: SlugType | null;
  type: string;
  isPatch?: boolean;
  disableIsMain?: boolean;
}>();

const DEFAULT_DATA = {
  is_main: 0,
  language: null,
  slug: null,
  type: props.type,
};

const payload = ref<SlugPayloadType>({ ...DEFAULT_DATA });

const languagesOptions = computed(() => [
  {
    option: "Português (BR)",
    value: "pt-br",
  },
  {
    option: "English (US)",
    value: "en-us",
  },
]);

const optionsYesNot = computed(() => [
  {
    option: t("common.labels.yes"),
    value: 1,
  },
  {
    option: t("common.labels.not"),
    value: 0,
  },
]);

function update(key: keyof typeof payload.value) {
  if (props.isPatch) {
    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function updateIsMain() {
  emit("update:isMain", { is_main: payload.value.is_main });
}

function resumeData() {
  if (!props.data) return;

  const { slug, is_main, language } = props.data;

  payload.value = {
    ...payload.value,
    slug,
    is_main,
    language,
  };
}

const debouceUpdate = debounce(update, 300);

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <section class="form-slug">
    <base-input
      v-model="payload.slug"
      :value="payload.slug"
      :placeholder="t('slug.placeholders.slug')"
      :mask="maskSlug"
      required
      @update:modelValue="debouceUpdate('slug')"
    >
      <template #label> {{ t("slug.labels.slug") }} * </template>
    </base-input>

    <base-select
      v-model="payload.is_main"
      :value="payload.is_main"
      :options="optionsYesNot"
      :is-empty="false"
      :disabled="disableIsMain"
      @change="updateIsMain()"
    >
      <template #label> {{ t("slug.labels.is_main") }}</template>
    </base-select>

    <base-select
      v-model="payload.language"
      :value="payload.language"
      :options="languagesOptions"
      :is-empty="true"
      @change="debouceUpdate('language')"
    >
      <template #label> {{ t("slug.labels.language") }}</template>
    </base-select>
  </section>
</template>

<style lang="scss" scoped>
.form-slug {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
